/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable react/jsx-props-no-spreading */
import RadioButtonGroup from '@mui/material/RadioGroup';
import * as _ from 'lodash';
import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import Dropzone from 'react-dropzone';
import { browserHistory } from 'react-router';

import { FileType } from '../../../../shared/fileUpload';
import Locator from '../../../../shared/Locator';
import { useParams } from '../../../contexts/RouterContext';
import { useDebouncedCallback } from '../../../hooks';
import useGetTeamsForProjectOrReleaseGroup from '../../../hooks/api/teams/useGetTeamsForProjectOrReleaseGroup';
import { get } from '../../../redux/api';
import { archiveUploadAccept, codeSentryArchiveUploadAccept, nuc, sbomUploadAccept } from '../../../utils';
import {
  AsyncSelect,
  FormLabelVariant,
  Icon,
  icons,
  LabeledRadio,
  LabeledTextInput,
} from '../../generic';
import Button, { BUTTON_SIZE, BUTTON_TYPE, ButtonGroup, IconButton, InlineLoaderButton } from '../../generic/Button';
import FormLabel from '../../generic/Forms/FormLabel';
import ProgressBar from '../../generic/ProgressBar';
import type { ProjectRevisions } from '../../../../typings/api';
import type { SelectOption } from '../../../types';

import { useFileUpload } from './hooks';

import styles from './DisplayProjectNav.module.less';

type Props = {
  onHide: () => void;
  shouldShow: boolean;
  fileType: FileType;
};

type Params = {
  project_id: string;
  reference_type: 'branch' | 'tag';
  reference: string;
  revision_id: string;
};

/**
 * Create the payload for the revisions endpoint to return 10 revisions
 * for the given search string, reference, and reference type.
 */
const createRevisionsEndpointPayload = ({
  search,
  reference,
  referenceType,
}: {
  search: string;
  reference: string;
  referenceType: Params['reference_type'];
}) => {
  return {
    locator: search,
    count: 10,
    offset: 0,
    refs: [reference],
    refs_type: referenceType,
  };
};

const ModalUploadRevision = ({ onHide, shouldShow, fileType }: Props) => {
  const routerParams = useParams<Params>();
  const [isCreatingNewVersion, setIsCreatingNewVersion] = useState(true);
  const versionButtonValue: 'NEW' | 'REPLACE' = isCreatingNewVersion ? 'NEW' : 'REPLACE';
  const [revisionVersion, setRevisionVersion] = useState('');
  const isSBOM = fileType === FileType.SBOM;
  const isBinary = fileType === FileType.Binary;
  const { data: teamsAndCounts } = useGetTeamsForProjectOrReleaseGroup(routerParams.project_id);

  const getFileTypeLabel = () => {
    if (isSBOM) {
      return 'SBOM';
    }
    if (isBinary) {
      return 'Binary';
    }
    return 'File';
  };

  const getAcceptedFormatsText = () => {
    if (isSBOM) {
      return 'CycloneDX(XML/JSON) and SPDX(JSON)';
    }
    if (isBinary) {
      return `Binary files (${Object.keys(codeSentryArchiveUploadAccept).join(', ')})`;
    }
    return 'Archive files (.zip, .tar.gz, etc.)';
  };

  const fileTypeLabel = getFileTypeLabel();
  let acceptExtensions = archiveUploadAccept;
  if (isSBOM) {
    acceptExtensions = sbomUploadAccept;
  } else if (isBinary) {
    acceptExtensions = codeSentryArchiveUploadAccept;
  }
  const { onDrop, size, file, resetFile, onUpload, isUploading } = useFileUpload(fileType);
  const onCancel = () => {
    resetFile();
    setRevisionVersion('');
    onHide();
  };
  const locator = new Locator(routerParams.project_id);
  const onUploadRevision = async () => {
    await onUpload({
      locator: routerParams.project_id,
      revision: revisionVersion,
      revisionType: versionButtonValue,
      selectedTeams: teamsAndCounts?.teams || [],
    });
    onCancel();
    const basePath = `/projects/${nuc(routerParams.project_id)}`;
    const refsSuffix = `${routerParams.reference_type || 'branch'}/${routerParams.reference || 'master'}/${nuc(revisionVersion)}`;
    const targetPath = isCreatingNewVersion
      ? `${basePath}/refs/${refsSuffix}`
      : `${basePath}/history/builds`;
    browserHistory.push(targetPath);
  };
  const [isRevisionNew, setIsRevisionNew] = useState(true);
  const shouldDisableSave = !file || !revisionVersion;
  const checkThatRevisionIsNew = useDebouncedCallback(async (revision: string) => {
    locator.revision = revision;
    try {
      const res = await get(`/api/revisions/${nuc(locator.toString())}/minimal`);
      setIsRevisionNew(res.status !== 200);
    } catch (error: unknown) {
      // `get` throws when the API returns 404, which means the revision does not exist
      setIsRevisionNew(true);
    }
  });

  return (
    <Modal
      onHide={() => {
        resetFile();
        onHide();
      }}
      show={shouldShow}
      className={styles.modalUploadSBOM}
    >
      <Modal.Header closeButton>
        <Modal.Title>Upload New {fileTypeLabel}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className={styles.section}>
          <div className={styles.subtitle}>{file ? 'File' : 'New File'}</div>
          {file ? (
            <div className={styles.droppedFile}>
              <span className={styles.acceptedFileIcon}>
                <Icon icon={isBinary ? icons.archive : icons.receiptSolid} />
              </span>
              <span className={styles.fileName}>{file.file.customName || ''}</span>
              <span className={styles.fileSize}>{size}</span>
              <span className={styles.acceptedFileIcon}>
                <IconButton icon={icons.xMark} onClick={resetFile} id="dropzone-remove-file" />
              </span>
            </div>
          ) : (
            <Dropzone onDrop={onDrop} accept={acceptExtensions}>
              {
                ({ getRootProps, getInputProps }) => (
                  <div className={styles.dropZone} {...getRootProps()}>
                    <input {...getInputProps()} />
                    <span className={styles.dropZoneTopLine}>
                      Drop your file here or <span className={styles.pseudoLink}>browse local files</span>
                    </span>
                    <p>
                      <b>Accepted formats:</b> {getAcceptedFormatsText()}
                    </p>
                  </div>
                )
              }
            </Dropzone>
          )}
        </div>
        {!isUploading && (
          <div className={styles.section}>
            <div className={styles.subtitle}>Version</div>
            <RadioButtonGroup
              name="versionGroup"
              defaultValue="NEW"
              row
              value={versionButtonValue}
              onChange={(_e, val: string) => {
                if (val === 'NEW' && !isCreatingNewVersion) {
                  setIsCreatingNewVersion(true);
                  setRevisionVersion('');
                  setIsRevisionNew(true);
                } else if (val === 'REPLACE' && isCreatingNewVersion) {
                  setIsCreatingNewVersion(false);
                  if (revisionVersion && isRevisionNew) {
                    // Only unset the version string if it would represent a new version
                    // This keeps the version string if it matches an existing revision,
                    // making it easier for the user to switch to the replace tab to replace it.
                    setRevisionVersion('');
                  }
                }
              }}
            >
              <LabeledRadio label="Create a new version" value="NEW" />
              {/* We only support replacing SBOMs for now */}
              {isSBOM && <LabeledRadio label="Replace an existing version" value="REPLACE" />}
            </RadioButtonGroup>
            {isCreatingNewVersion ? (
              <>
                <LabeledTextInput
                  labelVariant={FormLabelVariant.Filter}
                  label="Version"
                  placeholder='examples "1.2.3" or "v2 Alpha"'
                  value={revisionVersion}
                  spellCheck={false}
                  onChange={(val) => {
                    setRevisionVersion(val);
                    if (!_.isEmpty(val)) {
                      checkThatRevisionIsNew(val);
                    }
                  }}
                  isDisabled={!isCreatingNewVersion}
                />
                {revisionVersion && !isRevisionNew ? (
                  <p className={styles.error}>
                    This version already exists. Would you like to{' '}
                    <Button type="anchor" onClick={() => setIsCreatingNewVersion(false)}>
                      replace it
                    </Button>{' '}
                    instead?
                  </p>
                ) : (
                  <p className={styles.caption}>Give this version a unique number or description</p>
                )}
              </>
            ) : (
              <>
                <FormLabel label="Version to replace" htmlFor="replacement-version" variant={FormLabelVariant.Filter}>
                  <AsyncSelect
                    id="replacement-version"
                    value={{ label: revisionVersion, value: revisionVersion } as SelectOption}
                    loadOptions={async (inputValue: string) => {
                      const { data } = await get<ProjectRevisions>(
                        `/api/projects/${nuc(routerParams.project_id)}/revisions`,
                        false,
                        createRevisionsEndpointPayload({
                          search: inputValue,
                          reference: routerParams.reference,
                          referenceType: routerParams.reference_type,
                        }),
                      );
                      if (!data) {
                        return [];
                      }

                      return (data[routerParams.reference_type][routerParams.reference] || []).map((revision) => {
                        const revisionLocator = new Locator(revision.locator);
                        return {
                          label: revisionLocator.revision.value || '',
                          value: revisionLocator.revision.value || '',
                        };
                      });
                    }}
                    defaultOptions
                    onChange={(option) => {
                      if (option) {
                        setRevisionVersion(option.value);
                      }
                    }}
                  />
                </FormLabel>
                <p className={styles.caption}>Choose the version you wish to replace</p>
              </>
            )}
          </div>
        )}
        {isUploading && (
          <div className={styles.section}>
            <ProgressBar
              customMessage={`${isCreatingNewVersion ? 'Creating' : 'Replacing'} ${revisionVersion}`}
              bytesFinished={file?.status.bytesUploaded || 0}
              bytesTotal={file?.file.size || 0}
              hasFailed={!!file?.status.errorMessage}
              errorMessage={file?.status.errorMessage}
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button type={BUTTON_TYPE.SECONDARY} size={BUTTON_SIZE.LARGE} onClick={onCancel}>
            Cancel
          </Button>
          <InlineLoaderButton
            type={BUTTON_TYPE.PRIMARY}
            size={BUTTON_SIZE.LARGE}
            onClick={onUploadRevision}
            isDisabled={shouldDisableSave}
            isLoading={isUploading}
          >
            Upload
          </InlineLoaderButton>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalUploadRevision;
